<template>
  <v-row dense>
    <v-col cols="12">
      <v-select
        v-model="form.type_id"
        name="type_id"
        item-text="name"
        item-value="id"
        :items="types"
        :readonly="readonly"
        :label="`${$t('ITEM.TYPE')} *`"
        :rules="getRulesByName('type_id')"
        :error-messages="errors.type_id"
        clearable
        @change="updateValue('type_id', $event)"
      />
    </v-col>

    <v-col cols="12">
      <v-text-field
        v-model="form.serial_number"
        name="serial_number"
        counter="20"
        :label="`${$t('DEVICES.SERIAL_NUMBER')} *`"
        :readonly="readonly"
        :error-messages="errors.serial_number"
        :rules="getRulesByName('serial_number')"
        @input="updateValue('serial_number', $event)"
      />
    </v-col>

    <v-col cols="12">
      <phone-input
        v-model="form.phone"
        name="phone"
        :label="$t('DEVICES.PHONE')"
        :readonly="readonly"
        :error-messages="errors.phone"
        :rules="getRulesByName('phone')"
        @input="updateValue('phone', $event)"
      ></phone-input>
    </v-col>

    <v-col cols="12">
      <v-select
        v-model="form.states"
        name="states"
        item-text="name"
        item-value="id"
        :items="states"
        :readonly="readonly"
        :label="$t('DEVICES.STATES')"
        :rules="getRulesByName('states')"
        :error-messages="errors.states"
        clearable
        multiple
        @change="updateValue('states', $event)"
      />
    </v-col>

    <v-col cols="12">
      <v-textarea
        v-model="form.additional_info"
        name="additional_info"
        outlined
        rows="2"
        counter="255"
        :readonly="readonly"
        :label="$t('ITEM.ADDITIONAL_INFO')"
        :rules="getRulesByName('additional_info')"
        :error-messages="errors.additional_info"
        @input="updateValue('additional_info', $event)"
      />
    </v-col>

    <v-col cols="12">
      <v-switch
        v-model="form.available_for_market"
        name="available_for_market"
        :readonly="readonly"
        :label="$t('DEVICES.AVAILABLE_FOR_RENT')"
        :error-messages="errors.available_for_market"
        @change="updateValue('available_for_market', $event)"
      />
    </v-col>

    <v-col v-if="form.available_for_market" cols="12">
      <v-row dense>
        <v-col cols="12">
          <money-input
            v-model="form.sell_price"
            :label="$t('DEVICES.DEVICE_SELLING_PRICE')"
            :readonly="readonly"
            :error-messages="errors.sell_price"
            :rules="getRulesByName('sell_price')"
            @input="updateValue('sell_price', $event)"
          />
        </v-col>
        <v-col cols="12">
          <money-input
            v-model="form.subscription_fee"
            name="subscription_fee"
            :suffix="$t('PRICE.PER_MONTH', { currency: 'CHF' })"
            :label="$t('DEVICES.SUBSCRIPTION_FEE')"
            :readonly="readonly"
            :error-messages="errors.subscription_fee"
            :rules="getRulesByName('subscription_fee')"
            @input="updateValue('subscription_fee', $event)"
          />
        </v-col>
      </v-row>
    </v-col>
  </v-row>
</template>

<script>
import { dateFormat } from '@/modules/_global/filters';
import {
  notEmpty,
  maxLength,
  alphanumeric,
  maxLengthPriceNumber,
  minPriceNumeric
} from '@/modules/_global/variables/rules';
import formEditable from '@/modules/_global/mixins/form-editable';
import { numbersOnly } from '@/modules/_global/utils/events';
import { DevicesService } from '@/services/devices.service';
import { WarehouseDevicesService } from '@/services/warehouse-devices.service';
import MoneyInput from '@/modules/_global/_components/money/MoneyInput.vue';

export const PERSON = 'person';
export const WAREHOUSE = 'warehouse';
export const types = Object.freeze({
  PERSON,
  WAREHOUSE
});

export default {
  name: 'SubscriptionCreateForm',

  filters: {
    dateFormat
  },

  components: {
    MoneyInput,
    PhoneInput: () =>
      import('@/modules/_global/_components/phone/PhoneInput.vue')
  },

  mixins: [formEditable],

  props: {
    type: {
      type: String,
      default: WAREHOUSE,
      validator: prop => Object.values(types).includes(prop)
    }
  },

  data() {
    return {
      form: {
        type_id: null,
        serial_number: '',
        phone: '',
        states: [],
        additional_info: '',
        available_for_market: false,
        sell_price: 0,
        subscription_fee: 0
      },
      rules: {
        type_id: [notEmpty],
        serial_number: [notEmpty, alphanumeric, maxLength(20)],
        additional_info: [maxLength(255)],
        sell_price: [minPriceNumeric(0), maxLengthPriceNumber(6)],
        subscription_fee: [minPriceNumeric(0), maxLengthPriceNumber(6)]
      },
      types: [],
      states: []
    };
  },

  mounted() {
    this.getDeviceTypes().then(({ data }) => {
      this.types = data;
    });
    DevicesService.getStates().then(({ data }) => {
      this.states = data.data;
    });
  },

  methods: {
    getDeviceTypes() {
      switch (this.type) {
        case PERSON:
          return DevicesService.getTypes();
        case WAREHOUSE:
          return WarehouseDevicesService.getTypes();
      }
    },

    numbersOnly
  }
};
</script>
